import React, { useState, useEffect } from 'react'
import { useTournaments } from '../../contexts/TournamentContext'
import { useAuth } from '../../contexts/AuthContext'
import { useWebSocket } from '../../contexts/WebSocketContext'
import { showSuccess, showError } from '../Toast/Toast'
import InviteCodeModal from '../Modal/InviteCodeModal'
import QuestAccountModal from '../Modal/QuestAccountModal'
import './TournamentDetails.css'

const TournamentDetails = ({ tournamentId, fromTab, onBack, onNavigateToGame, onNavigateToAuth, onViewProfile }) => {
  const { user, isGuest } = useAuth()
  const {
    currentTournament,
    loading,
    error,
    getTournament,
    joinTournament,
    leaveTournament,
    getTournamentStandings,
    getTournamentBracket,
    getUserTournamentGames,
    clearError
  } = useTournaments()
  const { subscribe } = useWebSocket()

  const [standings, setStandings] = useState([])
  const [bracket, setBracket] = useState([])
  const [userTournamentGames, setUserTournamentGames] = useState([])
  const [activeTab, setActiveTab] = useState('info')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showQuestModal, setShowQuestModal] = useState(false)
  const [nowTick, setNowTick] = useState(Date.now())

  useEffect(() => {
    const interval = setInterval(() => setNowTick(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [])

  const getPregameTimeLeft = (game) => {
    if (!game) return 0
    if (game.status !== 'pregame') return 0

    // Tournament Logic (10 minute grace period)
    if (game.tournament_id) {
       const deadline = game.tournament_game_start_deadline 
         ? new Date(game.tournament_game_start_deadline).getTime()
         : new Date(game.created_at).getTime() + (10 * 60 * 1000)
       
       const now = nowTick
       return Math.max(0, Math.floor((deadline - now) / 1000))
    }

    // Default 30s
    if (!game.pregame_start_at) return 30
    const pregameStartMs = new Date(game.pregame_start_at).getTime()
    const elapsed = Math.max(0, Math.floor((nowTick - pregameStartMs) / 1000))
    return Math.max(0, 30 - elapsed)
  }

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  useEffect(() => {
    if (tournamentId) {
      getTournament(tournamentId)
      loadStandings()
      loadBracket()
      loadUserTournamentGames()
    }
  }, [tournamentId, getTournament])

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        clearError()
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [error, clearError])

  // WebSocket Updates
  useEffect(() => {
    if (!subscribe || !tournamentId) return

    const handleUpdate = (msg) => {
      try {
        let data = msg
        // If data is nested string (common in this app), parse it
        if (typeof msg.data === 'string') {
           data = JSON.parse(msg.data)
        }
        
        // Check both top-level and nested data for tournament_id
        const isRelevant = data.tournament_id === tournamentId || 
                          (data.data && data.data.tournament_id === tournamentId) ||
                          (msg.tournament_id === tournamentId) // In case it was on the root msg

        if (isRelevant) {
           console.log('Received tournament update via WS, refreshing...')
           getTournament(tournamentId)
           loadStandings()
           loadBracket()
           loadUserTournamentGames()
        }
      } catch (err) {
        // Ignore parsing errors
      }
    }

    const unsubs = [
        subscribe('tournament_update', handleUpdate),
        subscribe('tournament_started', handleUpdate),
        subscribe('match_update', handleUpdate)
    ]
    
    return () => unsubs.forEach(u => u())
  }, [subscribe, tournamentId])

  // Live tournament updates - refresh data every 30 seconds for actively running tournaments
  useEffect(() => {
    if (!tournamentId || !currentTournament) return

    const shouldAutoRefresh = currentTournament.status === 'active'

    if (!shouldAutoRefresh) return

    const refreshInterval = setInterval(async () => {
      try {
        // Silently refresh tournament data without showing loading states
        await Promise.all([
          getTournament(tournamentId),
          loadStandings(),
          loadBracket(),
          loadUserTournamentGames()
        ])
      } catch (err) {
        console.error('Failed to refresh tournament data:', err)
      }
    }, 30000) // Refresh every 30 seconds

    return () => clearInterval(refreshInterval)
  }, [tournamentId, currentTournament?.status, getTournament])

  const loadStandings = async () => {
    if (!tournamentId) return
    try {
      const standingsData = await getTournamentStandings(tournamentId)
      setStandings(standingsData)
    } catch (err) {
      console.error('Failed to load standings:', err)
    }
  }

  const loadBracket = async () => {
    if (!tournamentId) return
    try {
      const bracketData = await getTournamentBracket(tournamentId)
      setBracket(bracketData)
    } catch (err) {
      console.error('Failed to load bracket:', err)
    }
  }

  const loadUserTournamentGames = async () => {
    if (!user?.id) return
    try {
      const gamesData = await getUserTournamentGames()
      // Filter games for this tournament
      const tournamentGames = gamesData.filter(game =>
        game.tournament_id === tournamentId
      )
      setUserTournamentGames(tournamentGames)
    } catch (err) {
      console.error('Failed to load user tournament games:', err)
      // If authentication error, just set empty array instead of showing error
      if (err.message.includes('not authenticated')) {
        setUserTournamentGames([])
      }
    }
  }

  const navigateToGame = (gameId) => {
    if (gameId && onNavigateToGame) {
      onNavigateToGame(gameId)
    }
  }

  const refreshTournamentData = async () => {
    if (isRefreshing) return

    setIsRefreshing(true)
    try {
      await Promise.all([
        getTournament(tournamentId),
        loadStandings(),
        loadBracket(),
        loadUserTournamentGames()
      ])
    } catch (err) {
      console.error('Failed to refresh tournament data:', err)
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleJoinTournament = async () => {
    if (isGuest) {
      setShowQuestModal(true)
      return
    }

    if (tournament.access_type === 'invite_only') {
      setShowInviteModal(true)
      return
    }

    try {
      await joinTournament(tournamentId)
      showSuccess('Successfully joined tournament!')
    } catch (err) {
      console.error('Failed to join tournament:', err)
      showError('Failed to join tournament. Please try again.')
    }
  }

  const handleCreateAccount = () => {
    setShowQuestModal(false)
    onNavigateToAuth?.()
  }

  const handleInviteCodeSubmit = async (code) => {
    try {
      await joinTournament(tournamentId, code)
      showSuccess('Successfully joined tournament!')
      setShowInviteModal(false)
    } catch (err) {
      console.error('Failed to join tournament with code:', err)
      showError(err.message || 'Invalid invite code')
    }
  }

  const handleLeaveTournament = async () => {
    try {
      await leaveTournament(tournamentId)
      showSuccess('Successfully left tournament!')
    } catch (err) {
      console.error('Failed to leave tournament:', err)
      showError('Failed to leave tournament. Please try again.')
    }
  }

  const isUserRegistered = () => {
    if (!user?.id || !currentTournament?.players) return false
    return currentTournament.players.some(player => player.id === user.id)
  }

  const formatTimeControl = (timeControl) => {
    if (!timeControl) return 'Classic'
    const { initial_time, increment } = timeControl
    if (!initial_time && !increment) return 'Classic'
    const minutes = Math.floor(initial_time / 60)
    return `${minutes}+${increment}`
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      timeZone: 'Europe/Berlin',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }) + ' CET'
  }

  const formatPrizePool = (tournament) => {
    if (!tournament.is_money_prize || !tournament.prize_pool || !tournament.prize_pool.total_amount) {
      return 'TBD'
    }
    return `$${tournament.prize_pool.total_amount}`
  }

  const formatPrizeDistribution = (tournament) => {
    if (!tournament.is_money_prize || !tournament.prize_pool || !tournament.prize_pool.total_amount || !tournament.prize_pool.distribution) {
      return null
    }

    const positions = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th']
    const distribution = Array.isArray(tournament.prize_pool.distribution)
      ? tournament.prize_pool.distribution
      : JSON.parse(tournament.prize_pool.distribution || '[]')

    return (
      <div className="prize-distribution-list">
        {distribution.map((percentage, index) => {
          const amount = (tournament.prize_pool.total_amount * percentage) / 100
          return (
            <div key={index} className="prize-row">
              <span className="prize-position">{positions[index]}:</span>
              <span className="prize-amount">${amount}</span>
            </div>
          )
        })}
      </div>
    )
  }

  const formatEntryType = (tournament) => {
    if (tournament.access_type === 'invite_only') {
      return 'Invite Only'
    }
    if (tournament.entry_fee && tournament.entry_fee > 0) {
      return `$${tournament.entry_fee}`
    }
    return 'Free'
  }

  if (loading) {
    return (
      <div className="tournament-details-dtls">
        <div className="loading-spinner-dtls">
          <div className="spinner-dtls"></div>
          <p>Loading tournament details...</p>
        </div>
      </div>
    )
  }

  if (!currentTournament) {
    return (
      <div className="tournament-details-dtls">
        <div className="error-message-dtls">
          Tournament not found
        </div>
      </div>
    )
  }

  const tournament = currentTournament
  const isCreator = tournament.creator?.id === user?.id
  const isRegistered = isUserRegistered()

  return (
    <div className="tournament-details-dtls">
      <div className="tournament-details-header-dtls">
        <button className="back-btn-dtls" onClick={onBack}>
          ← Back to {fromTab === 'sitgo' ? 'Sit & Go' : 'Big Tournaments'}
        </button>
        <div className="tournament-title-section-dtls">
          <div className="tournament-title-header-dtls">
            <h1>{tournament.name}</h1>
            <div className="tournament-controls-dtls">
              {tournament.status === 'active' && (
                <div className="live-indicator-dtls">
                  <span className="live-dot-dtls"></span>
                  <span>LIVE</span>
                </div>
              )}
              <button
                className={`refresh-btn-dtls ${isRefreshing ? 'refreshing' : ''}`}
                onClick={refreshTournamentData}
                disabled={isRefreshing}
                title="Refresh tournament data"
              >
                {isRefreshing ? '↻' : '⟲'}
              </button>
            </div>
          </div>
          <p className="tournament-description-dtls">{tournament.description}</p>
        </div>

        {error && (
          <div className="error-message-dtls">
            {error}
          </div>
        )}
      </div>

      <div className="tournament-details-tabs-dtls">
        <button
          className={`tab-btn-dtls ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          Information
        </button>
        <button
          className={`tab-btn-dtls ${activeTab === 'players' ? 'active' : ''}`}
          onClick={() => setActiveTab('players')}
        >
          Players ({tournament.players_count})
        </button>
        {tournament.status !== 'registering' && (
          <>
            <button
              className={`tab-btn-dtls ${activeTab === 'bracket' ? 'active' : ''}`}
              onClick={() => setActiveTab('bracket')}
            >
              Bracket
            </button>
            {isRegistered && (
              <button
                className={`tab-btn-dtls ${activeTab === 'mygames' ? 'active' : ''}`}
                onClick={() => setActiveTab('mygames')}
              >
                My Games ({userTournamentGames.length})
              </button>
            )}
            <button
              className={`tab-btn-dtls ${activeTab === 'standings' ? 'active' : ''}`}
              onClick={() => setActiveTab('standings')}
            >
              Standings
            </button>
          </>
        )}
      </div>

      <div className="tournament-details-content-dtls">
        {activeTab === 'info' && (
          <div className="tournament-info-tab-dtls">
            <div className="tournament-info-grid-dtls">
              <div className="info-card-dtls">
                <h3>Tournament Details & Schedule</h3>
                <div className="info-row-dtls">
                  <span className="info-label-dtls">Type:</span>
                  <span className="info-value-dtls">{tournament.type.replace('_', ' ').toUpperCase()}</span>
                </div>
                <div className="info-row-dtls">
                  <span className="info-label-dtls">Time Control:</span>
                  <span className="info-value-dtls">{formatTimeControl(tournament.time_control)}</span>
                </div>
                <div className="info-row-dtls">
                  <span className="info-label-dtls">Status:</span>
                  <span className={`info-value-dtls status-${tournament.status}-dtls`}>
                    {tournament.status.toUpperCase()}
                  </span>
                </div>
                <div className="info-row-dtls">
                  <span className="info-label-dtls">Registered Players:</span>
                  <span className="info-value-dtls">
                    {tournament.max_players > 0
                      ? `${tournament.players_count}/${tournament.max_players}`
                      : tournament.players_count
                    }
                  </span>
                </div>
                <div className="info-row-dtls">
                  <span className="info-label-dtls">Start Time:</span>
                  <span className="info-value-dtls">{formatDate(tournament.start_time)}</span>
                </div>
                {tournament.registration_end && (
                  <div className="info-row-dtls">
                    <span className="info-label-dtls">Registration Ends:</span>
                    <span className="info-value-dtls">{formatDate(tournament.registration_end)}</span>
                  </div>
                )}
              </div>

              <div className="info-card-dtls">
                <h3>Prize Information</h3>
                <div className="info-row-dtls">
                  <span className="info-label-dtls">Entry Fee:</span>
                  <span className="info-value-dtls">{formatEntryType(tournament)}</span>
                </div>
                <div className="info-row-dtls">
                  <span className="info-label-dtls">Prize Pool:</span>
                  <span className="info-value-dtls">{formatPrizePool(tournament)}</span>
                </div>
                {formatPrizeDistribution(tournament) && (
                  <div className="info-row-dtls">
                    <span className="info-label-dtls">Prizes:</span>
                    <div className="info-value-dtls">
                      {formatPrizeDistribution(tournament)}
                    </div>
                  </div>
                )}
              </div>

              <div className="info-card-dtls">
                <h3>Players ({tournament.players_count})</h3>
                <div className="players-list-compact-dtls">
                  {tournament.players && tournament.players.length > 0 ? (
                    tournament.players
                      .sort((a, b) => (b.rating || 1200) - (a.rating || 1200))
                      .slice(0, 8).map((player, index) => (
                      <div key={player.id} className="player-row-compact-dtls">
                        <span className="player-rank-compact-dtls">#{index + 1}</span>
                        <div className="player-name-section-compact-dtls">
                          {player.is_gm && <span className="title-badge-compact gm">GM</span>}
                          {player.is_nm && <span className="title-badge-compact nm">NM</span>}
                          {player.is_im && <span className="title-badge-compact im">IM</span>}
                          {player.is_wfm && <span className="title-badge-compact wfm">WFM</span>}
                          <span
                            className="player-name-compact-dtls"
                            onClick={() => onViewProfile?.(player.username)}
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                          >
                            {player.username}
                          </span>
                          {player.account_type === 'premium' && (
                            <span className="premium-star-compact">★</span>
                          )}
                        </div>
                        <span className="player-rating-compact-dtls">{player.rating || 1200}</span>
                        {tournament.creator?.id === player.id && (
                          <span className="creator-badge-compact-dtls">Creator</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="empty-state-compact-dtls">
                      <p>No players yet</p>
                    </div>
                  )}
                  {tournament.players && tournament.players.length > 8 && (
                    <div className="player-row-compact-dtls">
                      <button
                        className="see-all-players-btn-dtls"
                        onClick={() => setActiveTab('players')}
                      >
                        See All ({tournament.players.length} players)
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {tournament.status === 'registering' && !isCreator && (
              isRegistered ? (
                <button
                  className="tournament-details-leave-btn-dtls"
                  onClick={handleLeaveTournament}
                  disabled={loading}
                >
                  Unregister
                </button>
              ) : (
                <button
                  className="tournament-details-join-btn-dtls"
                  onClick={handleJoinTournament}
                  disabled={loading || (tournament.max_players > 0 && tournament.players_count >= tournament.max_players)}
                >
                  {tournament.max_players > 0 && tournament.players_count >= tournament.max_players
                    ? 'Tournament Full'
                    : 'Register for Tournament'
                  }
                </button>
              )
            )}
          </div>
        )}

        {activeTab === 'players' && (
          <div className="tournament-players-tab-dtls">
            <div className="players-list-dtls">
              {tournament.players && tournament.players.length > 0 ? (
                tournament.players
                  .sort((a, b) => (b.rating || 1200) - (a.rating || 1200))
                  .map((player, index) => (
                  <div key={player.id} className="player-card-dtls">
                    <div className="player-info-dtls">
                      <span className="player-rank-dtls">#{index + 1}</span>
                      <div className="player-name-section-dtls">
                        {player.is_gm && <span className="title-badge gm">GM</span>}
                        {player.is_nm && <span className="title-badge nm">NM</span>}
                        {player.is_im && <span className="title-badge im">IM</span>}
                        {player.is_wfm && <span className="title-badge wfm">WFM</span>}
                        <span
                          className="player-name-dtls"
                          onClick={() => onViewProfile?.(player.username)}
                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          {player.username}
                        </span>
                        {player.account_type === 'premium' && (
                          <span className="premium-star-dtls">★</span>
                        )}
                      </div>
                      <span className="player-rating-dtls">{player.rating || 1200}</span>
                    </div>
                    {tournament.creator?.id === player.id && (
                      <span className="creator-badge-dtls">Creator</span>
                    )}
                  </div>
                ))
              ) : (
                <div className="empty-state-dtls">
                  <p>No players registered yet</p>
                  <p>Be the first to join!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'bracket' && tournament.status !== 'registering' && (
          <div className="tournament-bracket-tab-dtls">
            <div className="bracket-container-dtls">
              <div className="bracket-header-dtls">
                <h3>Tournament Bracket</h3>
                <p>Follow the tournament progress and see all matches</p>
              </div>

              {bracket && bracket.length > 0 ? (
                <div className="bracket-rounds-dtls">
                  {/* Group pairings by round */}
                  {[...new Set(bracket.map(pairing => pairing.round))].sort().map(round => (
                    <div key={round} className="bracket-round-dtls">
                      <h4>Round {round}</h4>
                      <div className="bracket-matches-dtls">
                        {bracket
                          .filter(pairing => pairing.round === round)
                          .map(pairing => {
                            const isUserInMatch = user && (
                              pairing.white_player_id === user.id ||
                              pairing.black_player_id === user.id
                            )
                            const gameStatus = pairing.game_id ?
                              (pairing.result ? 'completed' : 'active') :
                              (pairing.is_bye ? 'bye' : 'pending')

                            // Find the full game object if available in userTournamentGames to get status/ready state
                            const userGame = isUserInMatch && pairing.game_id 
                              ? userTournamentGames.find(g => g.id === pairing.game_id)
                              : null

                            return (
                              <div
                                key={pairing.id}
                                className={`bracket-match-card-dtls ${isUserInMatch ? 'user-match' : ''} ${pairing.game_id && !pairing.result ? 'clickable' : ''}`}
                                onClick={() => pairing.game_id && !pairing.result && isUserInMatch && navigateToGame(pairing.game_id)}
                              >
                                <div className="match-players-dtls">
                                  <div className={`match-player-dtls ${pairing.result === 'white_win' ? 'winner' : ''}`}>
                                    <span
                                      className="player-name-dtls"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        pairing.white_player?.username && onViewProfile?.(pairing.white_player.username)
                                      }}
                                      style={pairing.white_player?.username ? { cursor: 'pointer', textDecoration: 'underline' } : {}}
                                    >
                                      {pairing.white_player?.username || 'BYE'}
                                    </span>
                                    <span className="player-score-dtls">
                                      {pairing.is_bye ? 'BYE' :
                                       pairing.result === 'white_win' ? '1' :
                                       pairing.result === 'black_win' ? '0' : 
                                       pairing.result === 'draw' ? '½' : '-'}
                                    </span>
                                  </div>
                                  {!pairing.is_bye && (
                                    <>
                                      <div className="match-vs-dtls">vs</div>
                                      <div className={`match-player-dtls ${pairing.result === 'black_win' ? 'winner' : ''}`}>
                                        <span
                                          className="player-name-dtls"
                                          onClick={(e) => {
                                             e.stopPropagation()
                                             pairing.black_player?.username && onViewProfile?.(pairing.black_player.username)
                                          }}
                                          style={pairing.black_player?.username ? { cursor: 'pointer', textDecoration: 'underline' } : {}}
                                        >
                                          {pairing.black_player?.username || 'TBD'}
                                        </span>
                                        <span className="player-score-dtls">
                                          {pairing.result === 'black_win' ? '1' :
                                           pairing.result === 'white_win' ? '0' :
                                           pairing.result === 'draw' ? '½' : '-'}
                                        </span>
                                      </div>
                                    </>
                                  )}
                                </div>
                                <div className={`match-status-dtls ${gameStatus}`}>
                                  {userGame && userGame.status === 'pregame' ? (
                                     <div className="bracket-pregame-status">
                                        <span className="status-countdown">{formatCountdown(getPregameTimeLeft(userGame))}</span>
                                        {(() => {
                                           const isWhite = userGame.white_player_id === user?.id
                                           const myReady = isWhite ? userGame.white_ready : userGame.black_ready
                                            return myReady 
                                             ? <span className="status-ready">✅ Ready</span>
                                             : <span className="status-action">Check In!</span>
                                        })()}
                                     </div>
                                  ) : (
                                     gameStatus === 'bye' ? 'BYE' :
                                     gameStatus === 'completed' ? 'Completed' :
                                     gameStatus === 'active' ? (isUserInMatch ? 'Click to Play!' : 'Active') :
                                     'Pending'
                                  )}
                                </div>
                              </div>
                            )
                          })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state-dtls">
                  <p>Bracket will be available when the tournament starts</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'mygames' && isRegistered && (
          <div className="tournament-mygames-tab-dtls">
            <div className="mygames-container-dtls">
              <div className="mygames-header-dtls">
                <h3>My Tournament Games</h3>
                <p>Your active and completed games in this tournament</p>
              </div>

              {userTournamentGames && userTournamentGames.length > 0 ? (
                <div className="mygames-list-dtls">
                  {userTournamentGames.map(game => {
                    const isWhite = game.white_player_id === user?.id
                    const opponent = isWhite ? game.black_player : game.white_player
                    const isPlayable = game.status === 'active' || game.status === 'pregame' || game.status === 'waiting'
                    const isCompleted = game.status === 'completed' || game.status === 'abandoned' || game.status === 'cancelled'

                    let gameResult = ''
                    if (isCompleted && game.result) {
                      if (game.result === 'draw') {
                        gameResult = 'Draw'
                      } else if ((game.result === 'white_win' && isWhite) || (game.result === 'black_win' && !isWhite)) {
                        gameResult = 'Victory'
                      } else {
                        gameResult = 'Defeat'
                      }
                    }

                    return (
                      <div
                        key={game.id}
                        className={`mygame-card-dtls ${isPlayable ? 'active-game' : 'completed-game'} ${isPlayable ? 'clickable' : ''}`}
                        onClick={() => isPlayable && navigateToGame(game.id)}
                      >
                        <div className="mygame-info-dtls">
                          <div className="mygame-players-dtls">
                            <div className={`mygame-player-dtls ${isWhite ? 'current-user' : ''}`}>
                              <span className="player-color-dtls">White</span>
                              <span
                                className="player-name-dtls"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  !isWhite && opponent?.username && onViewProfile?.(opponent.username)
                                }}
                                style={!isWhite && opponent?.username ? { cursor: 'pointer', textDecoration: 'underline' } : {}}
                              >
                                {isWhite ? 'You' : (opponent?.username || 'Unknown')}
                              </span>
                            </div>
                            <div className="mygame-vs-dtls">vs</div>
                            <div className={`mygame-player-dtls ${!isWhite ? 'current-user' : ''}`}>
                              <span className="player-color-dtls">Black</span>
                              <span
                                className="player-name-dtls"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  isWhite && opponent?.username && onViewProfile?.(opponent.username)
                                }}
                                style={isWhite && opponent?.username ? { cursor: 'pointer', textDecoration: 'underline' } : {}}
                              >
                                {!isWhite ? 'You' : (opponent?.username || 'Unknown')}
                              </span>
                            </div>
                          </div>

                          <div className="mygame-details-dtls">
                            <span className="mygame-round-dtls">Round {game.tournament_round || 1}</span>
                            {isCompleted && (
                              <span className={`mygame-result-dtls ${gameResult.toLowerCase()}`}>
                                {gameResult}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className={`mygame-status-dtls ${game.status}`}>
                           {game.status === 'pregame' ? (
                             <div className="pregame-status-dtls">
                               <span className="countdown-dtls">Check-In: {formatCountdown(getPregameTimeLeft(game))}</span>
                               {(() => {
                                 const myReady = isWhite ? game.white_ready : game.black_ready
                                 return myReady 
                                   ? <span className="ready-badge-dtls">✅ Ready</span> 
                                   : <span className="action-needed-dtls">Create/Join Game to Check In</span>
                               })()}
                             </div>
                           ) : (
                             isPlayable ? 'Click to Play!' :
                             game.status === 'completed' ? 'Completed' :
                             game.status.charAt(0).toUpperCase() + game.status.slice(1)
                           )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="empty-state-dtls">
                  <p>No tournament games yet</p>
                  <p>Games will appear here when tournament rounds begin</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'standings' && tournament.status !== 'registering' && (
          <div className="tournament-standings-tab-dtls">
            <div className="standings-list-dtls">
              {standings && standings.length > 0 ? (
                standings.map((standing, index) => (
                  <div key={standing.id} className="standing-card-dtls">
                    <div className="standing-rank-dtls">#{standing.position}</div>
                    <div className="standing-info-dtls">
                      <span
                        className="standing-name-dtls"
                        onClick={() => standing.user?.username && onViewProfile?.(standing.user.username)}
                        style={standing.user?.username ? { cursor: 'pointer', textDecoration: 'underline' } : {}}
                      >
                        {standing.user?.username}
                      </span>
                      <div className="standing-stats-dtls">
                        <span>Points: {standing.points}</span>
                        <span>W: {standing.wins}</span>
                        <span>D: {standing.draws}</span>
                        <span>L: {standing.losses}</span>
                      </div>
                    </div>
                    {standing.prize_amount > 0 && (
                      <div className="standing-prize-dtls">
                        Prize: ${standing.prize_amount}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="empty-state-dtls">
                  <p>Standings will be available when the tournament starts</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <InviteCodeModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onConfirm={handleInviteCodeSubmit}
        loading={loading}
      />
      <QuestAccountModal
        isOpen={showQuestModal}
        onClose={() => setShowQuestModal(false)}
        onCreateAccount={handleCreateAccount}
      />
    </div>
  )
}

export default TournamentDetails